/**
 * Product Pricing Configuration
 * 
 * This file manages strikethrough (original/MRP) prices for products.
 * The strikethrough price is shown alongside the actual selling price to indicate discounts.
 * 
 * Configuration:
 * - Each entry maps a product_id to its strikethrough price
 * - Strikethrough prices are randomly generated between 15-40% higher than actual price
 * - Admin can manually edit these prices by modifying this file
 * - If a product_id is not in this list, no strikethrough price will be shown
 * 
 * Usage:
 * - Currently ALL products are configured to show strikethrough prices
 * - To remove strikethrough from a product, delete its entry from the productStrikePrices object
 * - To add strikethrough to a new product, add: product_id: calculated_price
 * 
 * Note: This pricing is only shown on product listing and detail pages.
 * It is NOT shown on cart, checkout, order history, or order details pages.
 */

// Helper function to calculate random strikethrough price
// Increases actual price by 15-40% randomly
const calculateStrikePrice = (actualPrice) => {
  const minIncrease = 1.15; // 15% increase
  const maxIncrease = 1.40; // 40% increase
  const randomMultiplier = minIncrease + Math.random() * (maxIncrease - minIncrease);
  return Math.round(actualPrice * randomMultiplier);
};

/**
 * Product Strikethrough Prices
 * 
 * Structure: { product_id: strikethrough_price }
 * 
 * Strikethrough prices are randomly generated between 15-40% higher than actual price.
 * Edit these values manually to adjust the strikethrough price for any product.
 * To remove strikethrough from a product, delete its entry below.
 * To add strikethrough to a new product, add a new line: product_id: strikethrough_price
 */
export const productStrikePrices = {
  // Products with strikethrough pricing (IDs 12-31)
  12: 499,   // Product ID 12
  13: 599,   // Product ID 13
  14: 499,   // Product ID 14
  15: 699,   // Product ID 15
//   16: 2183,   // Product ID 16
  17: 599,   // Product ID 17
  18: 599,   // Product ID 18
//   19: 3826,   // Product ID 19
  20: 599,   // Product ID 20
  21: 999,   // Product ID 21
  22: 999,   // Product ID 22
  23: 799,   // Product ID 23
  24: 899,   // Product ID 24
  25: 599,   // Product ID 25
  26: 599,   // Product ID 26
  27: 599,   // Product ID 27
  28: 799,   // Product ID 28
  29: 799,   // Product ID 29
  30: 699,   // Product ID 30
//   31: 3704,   // Product ID 31
  
  // Add more product IDs here as needed
  // Format: product_id: strikethrough_price,
};

/**
 * Get strikethrough price for a product
 * @param {number} productId - The product ID
 * @returns {number|null} - The strikethrough price or null if not configured
 */
export const getStrikePrice = (productId) => {
  return productStrikePrices[productId] || null;
};

/**
 * Check if product has strikethrough pricing enabled
 * @param {number} productId - The product ID
 * @returns {boolean} - True if product has strikethrough pricing
 */
export const hasStrikePrice = (productId) => {
  return productId in productStrikePrices;
};

/**
 * Add or update strikethrough price for a product
 * This is a helper for manual price management
 * @param {number} productId - The product ID
 * @param {number} strikePrice - The strikethrough price to set
 */
export const setStrikePrice = (productId, strikePrice) => {
  productStrikePrices[productId] = strikePrice;
};

/**
 * Generate random strikethrough price based on actual price
 * @param {number} actualPrice - The actual selling price
 * @returns {number} - Calculated strikethrough price (15-40% higher)
 */
export const generateStrikePrice = calculateStrikePrice;

/**
 * Bulk generate strikethrough prices for multiple products
 * @param {Array} products - Array of product objects with id and price
 * @returns {Object} - Object mapping product IDs to generated strikethrough prices
 */
export const bulkGenerateStrikePrices = (products) => {
  const prices = {};
  products.forEach(product => {
    if (product.id && product.price) {
      prices[product.id] = calculateStrikePrice(product.price);
    }
  });
  return prices;
};

export default productStrikePrices;
